'use client'

import { useEffect, useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import Script from 'next/script'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''

const loginSchema = z.object({
  email: z.string().email('Bitte geben Sie eine gueltige E-Mail-Adresse ein.'),
  password: z.string().min(1, 'Passwort ist erforderlich.'),
})

type LoginFormValues = z.infer<typeof loginSchema>

interface LoginFormProps {
  nextPath?: string
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          prompt: (callback?: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void
          renderButton: (element: HTMLElement, config: Record<string, unknown>) => void
        }
      }
    }
  }
}

export function LoginForm({ nextPath }: LoginFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const [retryAfter, setRetryAfter] = useState<number | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: process.env.NODE_ENV === 'development' ? 'demo@relay.dev' : '',
      password: process.env.NODE_ENV === 'development' ? 'demo-password-change-me' : '',
    },
  })

  const { isSubmitting } = form.formState

  // Countdown timer for rate-limit
  useEffect(() => {
    if (retryAfter === null || retryAfter <= 0) return

    const interval = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval)
          return null
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [retryAfter])

  const isRateLimited = retryAfter !== null && retryAfter > 0

  // Google Sign-In callback
  const handleGoogleCredentialResponse = useCallback(async (response: { credential: string }) => {
    setServerError(null)
    setGoogleLoading(true)

    try {
      const loginUrl = nextPath
        ? `/api/auth/google?next=${encodeURIComponent(nextPath)}`
        : '/api/auth/google'

      const res = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: response.credential, action: 'login' }),
        redirect: 'follow',
      })

      if (res.ok || res.redirected) {
        try {
          const body = await res.clone().json().catch(() => null)
          if (body?.needs_onboarding) {
            window.location.href = '/onboarding'
            return
          }
        } catch {
          // ignore
        }
        window.location.href = res.url || '/dashboard'
        return
      }

      if (res.status === 401) {
        setServerError('Kein Konto mit dieser Google-Adresse gefunden. Bitte registrieren Sie sich zuerst.')
        return
      }

      if (res.status === 403) {
        setServerError('Ihr Konto wurde gesperrt. Bitte kontaktieren Sie den Support.')
        return
      }

      const body = await res.json().catch(() => ({ message: 'Google-Anmeldung fehlgeschlagen.' }))
      setServerError(body.message || 'Google-Anmeldung fehlgeschlagen.')
    } catch {
      setServerError('Verbindungsfehler. Bitte versuchen Sie es erneut.')
    } finally {
      setGoogleLoading(false)
    }
  }, [nextPath])

  // Initialize Google Identity Services when script loads
  const initializeGoogle = useCallback(() => {
    if (!window.google || !GOOGLE_CLIENT_ID) return

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    })

    // Render the Google button
    const buttonContainer = document.getElementById('google-signin-button')
    if (buttonContainer) {
      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        locale: 'de',
        width: 320,
      })
    }
  }, [handleGoogleCredentialResponse])

  async function onSubmit(values: LoginFormValues) {
    setServerError(null)

    const loginUrl = nextPath
      ? `/api/auth/login?next=${encodeURIComponent(nextPath)}`
      : '/api/auth/login'

    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: values.email, password: values.password }),
      redirect: 'manual',
    })

    if (response.ok) {
      const body = await response.json().catch(() => null)
      if (body?.needs_onboarding) {
        window.location.href = '/onboarding'
        return
      }
      window.location.href = body?.redirectTo || '/dashboard'
      return
    }

    // Handle legacy redirect response (302)
    if (response.type === 'opaqueredirect' || response.redirected || response.status === 302) {
      window.location.href = '/dashboard'
      return
    }

    if (response.status === 401) {
      setServerError('Ungültige E-Mail-Adresse oder falsches Passwort.')
      return
    }

    if (response.status === 403) {
      setServerError('Ihr Konto wurde gesperrt. Bitte kontaktieren Sie den Support.')
      return
    }

    if (response.status === 422) {
      try {
        const body = (await response.json()) as {
          message?: string
          errors?: Record<string, string[]>
        }
        const errors = body.errors
        if (errors) {
          if (errors.email?.[0]) {
            form.setError('email', { message: errors.email[0] })
          }
          if (errors.password?.[0]) {
            form.setError('password', { message: errors.password[0] })
          }
          if (!errors.email && !errors.password && body.message) {
            setServerError(body.message)
          }
        } else {
          setServerError(body.message ?? 'Validierungsfehler. Bitte überprüfen Sie Ihre Eingaben.')
        }
      } catch {
        setServerError('Validierungsfehler. Bitte überprüfen Sie Ihre Eingaben.')
      }
      return
    }

    if (response.status === 429) {
      const retryHeader = response.headers.get('Retry-After')
      const retrySeconds = retryHeader ? parseInt(retryHeader, 10) : 60
      setRetryAfter(isNaN(retrySeconds) ? 60 : retrySeconds)
      setServerError(
        `Zu viele Anmeldeversuche. Bitte versuchen Sie es in ${isNaN(retrySeconds) ? 60 : retrySeconds} Sekunden erneut.`
      )
      return
    }

    setServerError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.')
  }

  return (
    <>
      {GOOGLE_CLIENT_ID && (
        <Script
          src="https://accounts.google.com/gsi/client"
          strategy="afterInteractive"
          onLoad={initializeGoogle}
        />
      )}

      {/* Google Sign-In */}
      {GOOGLE_CLIENT_ID && (
        <div className="mb-6">
          <div id="google-signin-button" className="flex justify-center" />
          {googleLoading && (
            <div className="mt-2 flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Google-Anmeldung...
            </div>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-3 text-slate-500">oder</span>
            </div>
          </div>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-Mail</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="sie@beispiel.de"
                    autoComplete="email"
                    autoFocus
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Passwort</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {serverError && (
            <div
              role="alert"
              className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive"
            >
              {isRateLimited && retryAfter !== null
                ? `Zu viele Anmeldeversuche. Bitte versuchen Sie es in ${retryAfter} Sekunden erneut.`
                : serverError}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || isRateLimited}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Anmeldung...
              </>
            ) : isRateLimited && retryAfter !== null ? (
              `Erneut versuchen in ${retryAfter}s`
            ) : (
              'Anmelden'
            )}
          </Button>

          {/* Demo-Login Buttons — only visible in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center mb-3">Demo-Zugaenge</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 text-xs"
                  disabled={isSubmitting}
                  onClick={() => {
                    form.setValue('email', 'demo@relay.dev')
                    form.setValue('password', 'demo-password-change-me')
                  }}
                >
                  Owner
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 text-xs"
                  disabled={isSubmitting}
                  onClick={() => {
                    form.setValue('email', 'member@relay.dev')
                    form.setValue('password', 'demo-password-change-me')
                  }}
                >
                  Mitarbeiter
                </Button>
              </div>
            </div>
          )}
        </form>
      </Form>
    </>
  )
}
